'use client';

import {
  Button,
  ButtonGroup,
  Card,
  Heading,
  InlineUnsafe,
  Text,
  Flex,
  Pill,
  Separator,
} from '@/_components';
import { useState, useMemo, useLayoutEffect, useContext, type FC } from 'react';
import { QuestionnaireListItem } from './components/QuestionnaireListItem';
import { toggleCardMore, getCardStatus } from './QuestionnaireCard.utils';
import { UserContext } from '@/_contexts/UserProvider';
import { getExpiredDaysAgo } from '@/_utils/dates';
import Link from 'next/link';
import type { QuestionnaireCardProps } from './QuestionnaireCard.types';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { getAdminDocumentDropOffPreviewLink } from '@/_utils/admin';

export const QuestionnaireCard: FC<QuestionnaireCardProps> = ({
  customStatus,
  description,
  dueDate,
  isPreview,
  questionnaire = { id: undefined, document_drop_off_enabled: false },
  sections = [],
  status,
  title,
}) => {
  const {
    selectedCompanyHash,
    selectedCompanyId,
    isUserReadOnly,
    isCompanyReadOnly,
  } = useContext(UserContext);
  const { previewCompany, isPreviewLoading } = useContext(UserPreviewContext);

  const { id: questionnaireId, document_drop_off_enabled: isDocumentDropOff } =
    questionnaire;

  const [limit, setLimit] = useState<3 | undefined>(3);

  const localSections = useMemo(() => {
    if (!Array.isArray(sections)) return [];
    if (!limit) return sections;

    const sortedSections = [...sections].sort(
      (a, b) => Number(a.isCompleted) - Number(b.isCompleted),
    );

    return sortedSections.slice(0, limit);
  }, [sections, limit]);

  const onToggleLimit = () => {
    setLimit(limit === 3 ? undefined : 3);
    toggleCardMore(questionnaireId || -1);
  };

  useLayoutEffect(() => {
    const limitStatus: boolean = getCardStatus(questionnaireId || -1);
    setLimit(limitStatus ? undefined : 3);
  }, [questionnaireId]);

  const isCompleted: boolean = status === 'completed';
  const isLocked: boolean = status === 'locked';
  const isInProgress: boolean = status === 'in_progress';
  const isOpened: boolean = status === 'opened' && !isCompleted;

  const dayDifference: number = getExpiredDaysAgo(dueDate);
  const dayDifferenceCount: number = Math.abs(getExpiredDaysAgo(dueDate));
  const hasDueDate: boolean =
    !Number.isNaN(dayDifferenceCount) && !isLocked && !isCompleted;
  const isViewOnly: boolean =
    isLocked || isCompleted || isUserReadOnly || isCompanyReadOnly;

  const isDueToday: boolean =
    !isCompleted && hasDueDate && dayDifferenceCount <= 1;
  const isExpired: boolean = !isCompleted && hasDueDate && dayDifference > 0;

  const hasAnyStatus: boolean =
    hasDueDate || isLocked || isInProgress || isOpened || isCompleted;

  const questionnaireLink: string = useMemo(
    () =>
      isDocumentDropOff
        ? `/document-drop-off?questionnaireId=${questionnaireId}&companyId=${selectedCompanyId}`
        : `/questionnaire/${selectedCompanyHash}/?questionnaireId=${questionnaireId}`,
    [
      questionnaireId,
      isDocumentDropOff,
      selectedCompanyId,
      selectedCompanyHash,
    ],
  );

  const questionnaireAdminLink: string = useMemo(
    () =>
      typeof previewCompany?.id === 'number'
        ? isDocumentDropOff
          ? getAdminDocumentDropOffPreviewLink(
              questionnaireId,
              previewCompany?.id,
            ) || ''
          : `/preview/${previewCompany?.id}/questionnaire/${questionnaireId}`
        : '',
    [previewCompany?.id, questionnaireId, isDocumentDropOff],
  );

  return (
    <Card padding={8} gap={10} data-testid="ClientDashboard-questionnaire-card">
      {hasAnyStatus && (
        <Flex gap={2}>
          {hasDueDate && (
            <Pill
              intent={isDueToday || isExpired ? 'danger' : 'warning'}
              isRound={true}
              label={
                isExpired
                  ? `Overdue ${dayDifferenceCount + 1} day${dayDifferenceCount > 1 ? 's' : ''} ago`
                  : isDueToday
                    ? 'Due Today!'
                    : `Due in ${dayDifferenceCount + 1} day${dayDifferenceCount > 1 ? 's' : ''}`
              }
              iconLeft={isDueToday ? 'siren' : 'clock'}
              isDark={isDueToday}
            />
          )}
          {isLocked && (
            <Pill
              intent="default"
              label="Locked"
              iconLeft="lock"
              isRound={true}
            />
          )}
          {isOpened && (
            <Pill
              intent="info"
              label="Opened"
              iconLeft="clock"
              isRound={true}
            />
          )}
          {isCompleted && (
            <Pill
              intent="success"
              label="Completed"
              iconLeft="check-circle-filled"
              isRound={true}
            />
          )}
          {isInProgress && (
            <Pill
              intent="info"
              label="In Progress"
              iconLeft="clock"
              isRound={true}
            />
          )}
        </Flex>
      )}
      <Flex isColumn={true} gap={4}>
        <Flex isBetween={true} gap={4}>
          <Heading
            level={2}
            color={isLocked ? 'disabled' : 'primary'}
            breakWords={true}
          >
            {title}
          </Heading>
          {!!customStatus && (
            <Pill
              intent="info"
              label={customStatus}
              size="large"
              isRound={true}
            />
          )}
        </Flex>
        {description && (
          <Text type="small" color={isLocked ? 'disabled' : 'primary'}>
            <InlineUnsafe text={description} />
          </Text>
        )}
      </Flex>
      <Flex isColumn={true} gap={10}>
        {localSections?.map((section, key) => {
          return (
            <QuestionnaireListItem
              {...section}
              key={key}
              companyHash={isPreview ? previewCompany?.id : selectedCompanyHash}
              questionnaireId={questionnaireId}
              isPreview={isPreview}
            />
          );
        })}
        {localSections?.length > 3 ? (
          <Flex gap={6} isYCentered={true} onClick={onToggleLimit}>
            <Separator />
            <ButtonGroup>
              <Button
                value="Show More"
                isIconOnly={true}
                intent="ghost"
                iconLeft={!limit ? 'caret-up' : 'caret-down'}
                size="tiny"
              />
            </ButtonGroup>
            <Separator />
          </Flex>
        ) : null}
      </Flex>
      {questionnaireId && (
        <ButtonGroup align="right">
          {isPreview ? (
            <Link
              href={questionnaireAdminLink}
              tabIndex={-1}
              aria-disabled={!questionnaireAdminLink}
            >
              <Button
                value="Preview Questionnaire"
                intent="secondary"
                isDisabled={!questionnaireAdminLink}
                isLoading={isPreviewLoading}
                iconRight="eye"
              />
            </Link>
          ) : (
            <Link href={questionnaireLink} tabIndex={-1}>
              <Button
                value={isViewOnly ? 'View' : isOpened ? 'Continue' : 'Start'}
                intent={isViewOnly ? 'secondary' : 'primary'}
                iconRight={isViewOnly ? 'eye' : 'arrow-right'}
                data-testid={
                  isOpened
                    ? 'ClientDashboard-questionnaire-continue-button'
                    : 'ClientDashboard-questionnaire-start-button'
                }
              />
            </Link>
          )}
        </ButtonGroup>
      )}
    </Card>
  );
};
